import { useState } from 'react';
import { CheckCircleOutlined, MessageOutlined, SendOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Skeleton, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { HanMucHoacCauGoc } from '../../../components/shared';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';
import { useHoiTroLy, useLichSuTroLy } from '../hooks/useThamDinh';
import type { TrichDanTroLy } from '../types';

export interface HopTroLyProps {
  phienId: string;
  /** Mở tệp tại một trích dẫn; view quyết điều hướng. */
  onMoTrichDan?: (tep: number, trich: string) => void;
}

/** Trần câu hỏi, khớp `parseCauHoi` của backend. */
const TRAN_CAU_HOI = 2000;

function DanhSachTrichDan({ ds, onMo }: { ds: readonly TrichDanTroLy[]; onMo?: HopTroLyProps['onMoTrichDan'] }) {
  const { t, locale } = useT();
  if (ds.length === 0) return null;
  return (
    <div className="rounded border border-default bg-subtle p-2">
      <p className="text-2xs font-medium text-content-muted">{t('thamDinh.raSoat.troLyTrichDan')}</p>
      <ul className="mt-1 space-y-1">
        {ds.map((d, i) => (
          <li key={`${d.tep}-${i}`} className="text-sm">
            <button
              type="button"
              className="text-start underline decoration-dotted underline-offset-2 hover:text-primary-700"
              onClick={() => onMo?.(d.tep, d.trich)}
            >
              “{d.trich}”
            </button>
            <span className="ms-1 text-2xs text-content-muted">
              {d.tenTep}
              {d.trang !== null && `, ${t('thamDinh.raSoat.troLyTrangSo', { so: soVN(d.trang, locale) })}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Hỏi–đáp trên hồ sơ (đặc tả mục 10.5, Mốc 11).
 *
 * Ba điều quyết định cách dựng:
 *
 * - **Chữ chảy dần**: câu trả lời hiện ngay từ mẩu đầu, không chờ xong. Đó là lý do dùng SSE thay vì một
 *   `POST` thường — một màn hình chờ 40 giây rồi đổ ra cả đoạn là trải nghiệm của một API chậm.
 * - **Trích dẫn bấm được**: mỗi trích dẫn mở tệp tại chỗ ấy. Câu trả lời của AI chỉ dùng được khi kiểm được,
 *   và kiểm được nghĩa là bấm một lần tới đúng đoạn trong hồ sơ.
 * - **Lịch sử chỉ người hỏi thấy** (đặc tả mục 10.5): câu hỏi giữa chừng là bản nháp của một suy nghĩ. Bày
 *   nháp của người thẩm định ra trước mặt người phê duyệt là đổi cách người ta hỏi — họ sẽ hỏi ít đi.
 */
export function HopTroLy({ phienId, onMoTrichDan }: HopTroLyProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const su = useLichSuTroLy(phienId);
  const { luot, dangHoi, hoi, dung } = useHoiTroLy(phienId);
  const [cauHoi, setCauHoi] = useState('');

  const dsSu = su.data?.items ?? [];

  return (
    <div className="space-y-3">
      <p className="text-sm text-content-muted">{t('thamDinh.raSoat.troLyMo')}</p>

      <div className="space-y-2">
        <label htmlFor="tro-ly-cau-hoi" className="block text-2xs font-medium text-content-muted">
          {t('thamDinh.raSoat.troLyOHoi')}
        </label>
        <Textarea
          id="tro-ly-cau-hoi"
          rows={2}
          value={cauHoi}
          maxLength={TRAN_CAU_HOI}
          placeholder={t('thamDinh.raSoat.troLyVd')}
          disabled={dangHoi}
          onChange={(e) => setCauHoi(e.target.value)}
        />
        <div className="flex justify-end gap-2">
          {dangHoi && (
            <Button variant="outline" size="sm" onClick={dung}>{t('thamDinh.raSoat.troLyDung')}</Button>
          )}
          <Button
            variant="primary"
            size="sm"
            icon={<SendOutlined />}
            loading={dangHoi}
            disabled={cauHoi.trim().length === 0}
            onClick={() => { void hoi(cauHoi.trim()); setCauHoi(''); }}
          >
            {t('thamDinh.raSoat.troLyGui')}
          </Button>
        </div>
      </div>

      {/* Lượt đang chạy: chặng tiến trình, chữ chảy dần, rồi trích dẫn */}
      {luot.cauHoi && (
        <div className="space-y-2 rounded-lg border border-primary-200 bg-primary-50 p-3">
          <p className="text-sm font-medium text-primary-800">{luot.cauHoi}</p>

          {luot.chang.length > 0 && !luot.xong && (
            <ul className="space-y-0.5">
              {luot.chang.map((c) => (
                <li key={c.step} className="flex items-center gap-1.5 text-2xs">
                  {c.status === 'ok' ? <CheckCircleOutlined className="text-success-600" />
                    : c.status === 'warn' ? <WarningOutlined className="text-warning-600" />
                      : <span className="size-1.5 animate-pulse rounded-full bg-primary-500" aria-hidden="true" />}
                  <span className={cn(c.status === 'warn' ? 'text-warning-800' : 'text-content-muted')}>
                    {c.detail}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/* Cảnh báo giữ lại cả khi đã xong: "đã cắt phần cuối" là thứ người đọc cần biết */}
          {luot.xong && luot.chang.filter((c) => c.status === 'warn').map((c) => (
            <p key={c.step} className="text-sm text-warning-800">{c.detail}</p>
          ))}

          {luot.dap && <div className="whitespace-pre-wrap text-sm">{luot.dap}</div>}

          {luot.loi && (
            <HanMucHoacCauGoc cau={luot.loi} bienThe="gon">
              <p className="rounded border border-danger-200 bg-danger-50 p-2 text-sm text-danger-700">
                {luot.loi}
              </p>
            </HanMucHoacCauGoc>
          )}

          {luot.xong && <DanhSachTrichDan ds={luot.trichDan} onMo={onMoTrichDan} />}
        </div>
      )}

      {/* Lịch sử: chỉ người hỏi thấy */}
      <section aria-label={t('thamDinh.raSoat.troLyLichSu')} className="space-y-2">
        <h3 className="text-sm font-medium">{t('thamDinh.raSoat.troLyLichSu')}</h3>
        {su.isPending && <Skeleton className="h-16 w-full" />}
        {su.error && <p className="text-sm text-danger-700">{dichLoi(su.error)}</p>}
        {!su.isPending && !su.error && dsSu.length === 0 && (
          <p className="text-sm text-content-muted">{t('thamDinh.raSoat.troLyChuaHoi')}</p>
        )}
        <ul className="space-y-2">
          {dsSu.map((h) => (
            <li key={h.id} className="space-y-1 rounded border border-default p-2">
              <div className="flex items-start gap-1.5">
                <MessageOutlined className="mt-0.5 shrink-0 text-content-muted" />
                <p className="min-w-0 flex-1 text-sm font-medium">{h.cau_hoi}</p>
                <span className="shrink-0 text-2xs text-content-muted">#{soVN(h.thu_tu, locale)}</span>
              </div>
              {h.trang_thai === 'dang_chay' ? (
                <p className="text-sm text-content-muted">{t('thamDinh.raSoat.troLyDangChay')}</p>
              ) : h.trang_thai === 'loi' ? (
                <HanMucHoacCauGoc cau={h.loi} bienThe="gon">
                  <p className="text-sm text-danger-700">{h.loi}</p>
                </HanMucHoacCauGoc>
              ) : (
                <>
                  <div className="whitespace-pre-wrap text-sm text-content-secondary">{h.cau_tra_loi}</div>
                  <DanhSachTrichDan ds={h.trich_dan} onMo={onMoTrichDan} />
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
